import { urlState } from "app/client/models/gristUrlState";
import { EmailVerifiedPage } from "app/client/ui/EmailVerifiedPage";
import { ForgotPasswordPage } from "app/client/ui/ForgotPasswordPage";
import { LoginPage } from "app/client/ui/LoginPage";
import * as css from "app/client/ui/LoginPagesCss";
import { OAuthAPI } from "app/client/ui/OAuthAPI";
import { ConsentForm, SelectAccountForm } from "app/client/ui/OAuthForms";
import { SignupPage } from "app/client/ui/SignupPage";
import { getPageTitleSuffix } from "app/common/gristUrls";
import { LoginPageConfig } from "app/common/LoginPageConfig";
import { getGristConfig } from "app/common/urlUtils";
import { dom } from "grainjs";

export function createLoginPage() {
  return dom.domComputed(urlState().state, (state) => {
    const { login } = state;

    if (login) {
      switch (login) {
        case "login": {
          return dom.create(LoginPage);
        }
        case "signup": {
          return dom.create(SignupPage);
        }
        case "verified": {
          return dom.create(EmailVerifiedPage);
        }
        case "forgot-password": {
          return dom.create(ForgotPasswordPage);
        }
        default: {
          // Make sure we don't forget to handle a page. Leaving out this default
          // case is typically enough for TypeScript to catch such errors, but
          // since `undefined` is a valid `DomContents` value and is implicitly
          // returned in the absence of a default case, we need to explicitly
          // check that the type of the page is `never`.
          const exhaustiveCheck: never = login;
          throw new Error(`Unhandled login page: ${exhaustiveCheck}`);
        }
      }
    }

    const { oauth } = getGristConfig() as LoginPageConfig;
    if (oauth?.interaction) {
      const { client, interaction: { cid, prompt } } = oauth;
      const { name } = prompt;

      switch (name) {
        case "select_account":
        case "consent": {
          const api = new OAuthAPI(cid, client);
          const suffix = getPageTitleSuffix(getGristConfig());
          document.title = `Sign in${suffix}`;
          return css.pageContainer(
            css.flexJustifyCenter(
              css.formContainer(
                name === "select_account"
                  ? dom.create(SelectAccountForm, { api, client, prompt })
                  : dom.create(ConsentForm, { api, client, prompt })
              )
            )
          );
        }
        default: {
          const exhaustiveCheck: never = name;
          throw new Error(`Unhandled OAuth interaction prompt: ${exhaustiveCheck}`);
        }
      }
    }

    throw new Error("Page not found");
  });
}
